<script setup lang="ts">
import { computed } from 'vue'
import { BellAlertIcon } from '@heroicons/vue/24/outline'
import { deriveConsolidatedRow } from '@/interfaces/entities/management'
import { useManagementStore } from '@/store/management'

const props = defineProps<{ areaId: number }>()

const store = useManagementStore()

const area = computed(() => store.nodesById[props.areaId] ?? null)
const indicators = computed(() => store.areaIndicators(props.areaId))
const ops = computed(() => (indicators.value ? deriveConsolidatedRow(indicators.value) : null))
const classifications = computed(() => indicators.value?.alarm_classifications ?? [])

function formatDuration(minutes: number | null): string {
  if (minutes === null || minutes === undefined) return '—'
  if (minutes < 60) return `${Math.round(minutes)} min`
  const hours = Math.floor(minutes / 60)
  const mins = Math.round(minutes % 60)
  return mins ? `${hours}h ${mins}min` : `${hours}h`
}

function formatPercent(value: number | null): string {
  return value === null || value === undefined ? '—' : `${value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
}
</script>

<template>
  <div class="border-2 border-primary/30 bg-white">
    <header class="flex items-center gap-2 border-b border-stroke bg-primary/5 px-4 py-3">
      <BellAlertIcon class="h-5 w-5 text-primary" />
      <h3 class="text-base font-bold text-slate-800">Detalhe de paradas — {{ area?.name ?? 'Área' }}</h3>
    </header>

    <div v-if="!indicators" class="p-6 text-center text-sm text-slate-400">Carregando indicadores da área…</div>

    <template v-else>
      <!-- Resumo da linha (como na planilha) -->
      <div class="grid grid-cols-2 gap-px bg-stroke sm:grid-cols-4">
        <div class="bg-white px-4 py-3">
          <p class="text-[11px] font-medium uppercase tracking-wide text-slate-500">Utilização</p>
          <p class="mt-0.5 text-2xl font-extrabold text-slate-900">{{ formatPercent(ops?.utilizacao ?? null) }}</p>
        </div>
        <div class="bg-white px-4 py-3">
          <p class="text-[11px] font-medium uppercase tracking-wide text-amber-700">Tempo total de parada</p>
          <p class="mt-0.5 text-2xl font-extrabold text-amber-700">{{ formatDuration(ops?.tempo_parada_min ?? null) }}</p>
        </div>
        <div class="bg-white px-4 py-3">
          <p class="text-[11px] font-medium uppercase tracking-wide text-slate-500">Quantidade de paradas</p>
          <p class="mt-0.5 text-2xl font-extrabold text-slate-900">{{ ops?.paradas ?? '—' }}</p>
        </div>
        <div class="bg-white px-4 py-3">
          <p class="text-[11px] font-medium uppercase tracking-wide text-slate-500">Demais alarmes</p>
          <p class="mt-0.5 text-2xl font-extrabold text-slate-900">{{ ops?.demais_alarmes ?? '—' }}</p>
        </div>
      </div>

      <!-- Paradas por tipo (classification types do período) -->
      <div class="p-4">
        <h4 class="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Tipo de parada</h4>
        <div v-if="classifications.length" class="overflow-x-auto border border-stroke">
          <table class="w-full text-left text-sm">
            <thead class="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th class="px-4 py-2.5">Tipo de parada</th>
                <th class="px-4 py-2.5 text-right">Quantidade</th>
                <th class="px-4 py-2.5 text-right">Horas</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in classifications" :key="item.classification" class="border-t border-stroke">
                <td class="px-4 py-2.5 font-medium text-slate-700">{{ item.classification }}</td>
                <td class="px-4 py-2.5 text-right text-base font-bold text-slate-900">{{ item.count }}</td>
                <td class="px-4 py-2.5 text-right text-base font-bold text-amber-700">{{ formatDuration(item.stopped_minutes) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="text-sm text-slate-500">Nenhuma parada classificada neste período.</p>
      </div>
    </template>
  </div>
</template>
